// apps/patient/src/components/profile/SubscriptionSection.tsx
//
// Ready2Exercise standalone subscription ($20/month, 7-day trial through Stripe Checkout).
// Rendered on the Profile page and the /subscribe page for standalone exercise users only;
// org-linked patients never see it. Cancel / card changes happen in the Stripe Customer Portal.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, Loader2 } from 'lucide-react';
import { formatDate } from '@prism-ehr/shared';
import { patientApiService } from '../../services/api';
import { usePatient } from '../../contexts/PatientContext';
import { waitForExerciseSubscription } from '../../utils/subscriptionPoll';

export const EXERCISE_PLAN_NAME = 'Ready2Exercise';
export const EXERCISE_PLAN_PRICE = '$20/month';

interface SubscriptionSectionProps {
  user: any;
  /** Where Stripe sends the user back after checkout. Must be one the backend whitelists. */
  returnPath?: '/profile' | '/subscribe';
  /** Called once a `?payment=success` return has been confirmed against /auth/me. */
  onConfirmed?: () => void;
  /** Heading shown above the plan card. */
  title?: string;
}

export function SubscriptionSection({
  user,
  returnPath = '/profile',
  onConfirmed,
  title = 'Subscription'
}: SubscriptionSectionProps) {
  const { refreshPatient } = usePatient();
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentParam = searchParams.get('payment');

  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(paymentParam === 'success');
  const [error, setError] = useState<string | null>(null);
  const confirmStarted = useRef(false);

  const app = user?.exercise_app || {};
  const status: string = app.subscription_status || 'none';
  const hasCustomer = !!app.stripe_customer_id;

  // Returned from Stripe Checkout: wait for the webhook, then refresh the profile.
  useEffect(() => {
    if (paymentParam !== 'success' || confirmStarted.current) return;
    confirmStarted.current = true;
    let cancelled = false;

    (async () => {
      const profile = await waitForExerciseSubscription();
      if (cancelled) return;
      await refreshPatient();
      setConfirming(false);
      const next = new URLSearchParams(searchParams);
      next.delete('payment');
      setSearchParams(next, { replace: true });
      if (profile) {
        onConfirmed?.();
      } else {
        setError('Payment received, still confirming your subscription. Refresh this page in a moment.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [paymentParam]);

  const handleManage = async () => {
    try {
      setBusy(true);
      setError(null);
      const result = await patientApiService.createExerciseBillingPortalSession();
      if (result?.url) {
        window.location.href = result.url;
      } else {
        setError('Could not open the billing portal. Please try again.');
        setBusy(false);
      }
    } catch (err) {
      console.error('Billing portal error:', err);
      setError('Could not open the billing portal. Please try again.');
      setBusy(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setBusy(true);
      setError(null);
      const result = await patientApiService.createExerciseCheckoutSession(user?.email, returnPath);
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
      } else {
        setError('Could not start checkout. Please try again.');
        setBusy(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Could not start checkout. Please try again.');
      setBusy(false);
    }
  };

  const statusLine = (() => {
    switch (status) {
      case 'trial':
        return app.trial_expires_at
          ? `Free trial, ends ${formatDate(app.trial_expires_at, 'short')}`
          : 'Free trial';
      case 'active':
        if (app.cancel_at_period_end && app.current_period_end) {
          return `Cancels on ${formatDate(app.current_period_end, 'short')}`;
        }
        return app.current_period_end
          ? `Active, renews on ${formatDate(app.current_period_end, 'short')}`
          : 'Active';
      case 'past_due':
        return 'Payment failed. Update your card to keep access.';
      case 'access_code':
        return 'Access granted with a code';
      case 'cancelled':
      case 'none':
      default:
        return 'No active subscription';
    }
  })();

  const showSubscribe = status === 'none' || status === 'cancelled';
  const showManage = hasCustomer && status !== 'access_code';

  return (
    <div className="p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <CreditCard className="w-5 h-5 mr-2 text-gray-400" />
        {title}
      </h3>

      <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
        <div className="flex justify-between items-start gap-4">
          <div>
            <p className="font-medium text-gray-900">{EXERCISE_PLAN_NAME}</p>
            <p className="text-sm text-gray-600 mt-1">
              {confirming ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Confirming your subscription...
                </span>
              ) : (
                statusLine
              )}
            </p>
          </div>
          <div className="text-right whitespace-nowrap">
            <span className="text-xl font-bold">$20</span>
            <span className="text-gray-500 text-sm">/month</span>
          </div>
        </div>

        {!confirming && (showSubscribe || showManage) && (
          <div className="mt-4 flex flex-wrap gap-3">
            {showSubscribe && (
              <button
                type="button"
                onClick={handleSubscribe}
                disabled={busy}
                className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Subscribe, {EXERCISE_PLAN_PRICE}
              </button>
            )}
            {showManage && (
              <button
                type="button"
                onClick={handleManage}
                disabled={busy}
                className="px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-100 disabled:opacity-50 flex items-center gap-2"
              >
                {busy && !showSubscribe && <Loader2 className="h-4 w-4 animate-spin" />}
                Manage subscription
              </button>
            )}
          </div>
        )}

        {showSubscribe && !confirming && (
          <p className="text-xs text-gray-500 mt-3">
            7-day free trial, then {EXERCISE_PLAN_PRICE}. Cancel anytime from this page.
          </p>
        )}
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
    </div>
  );
}
